import { useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, cn, formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { fetchExpense, homeExpenseQueryKeys } from "@apps/dashboard/lib/home-expense-api";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { authClient } from "@apps/dashboard/lib/auth-client";
import type { ExpenseDetail } from "@apps/dashboard/lib/home-expense-types";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

type LocationState = { expense?: ExpenseDetail };

export default function ExpensePaidFeedbackPage(): JSX.Element {
  const { houseId, expenseId } = useParams<{ houseId: string; expenseId: string }>();
  const { t, locale } = useI18n();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  useSyncHouseFromParams(houseId);

  const { data: sessionData } = authClient.useSession();
  const currentUserId = sessionData?.user?.id ?? null;

  const fromState = state?.expense;
  const expenseQuery = useQuery({
    queryKey:
      houseId && expenseId
        ? homeExpenseQueryKeys.expense(locale, houseId, expenseId)
        : ["expense-paid-fb", "noop"],
    queryFn: () => {
      if (!houseId || !expenseId) throw new Error("missing route params");
      return fetchExpense(locale, houseId, expenseId);
    },
    enabled: Boolean(houseId && expenseId && !fromState),
  });

  const expense = fromState ?? expenseQuery.data ?? null;
  const loading = !fromState && expenseQuery.isLoading;
  const error = !fromState && expenseQuery.isError;

  const effectiveMinor = expense ? expenseEffectiveMinor(expense) : 0;

  const contributionPercent = useMemo(() => {
    if (!expense?.splits?.length || !currentUserId) return null;
    const row = expense.splits.find((s) => s.userId === currentUserId);
    if (!row) return null;
    return Math.round(row.shareBps / 100);
  }, [expense, currentUserId]);

  const paidByLabel = useMemo(() => {
    if (!expense) return "";
    if (expense.paidFromJointAccount === true) {
      return t("dashboardUi.expenses.form.paidByJointOption");
    }
    if (expense.paidBy?.name) {
      return expense.paidBy.name;
    }
    return t("dashboardUi.expenses.detail.notApplicable");
  }, [expense, t]);

  const categoryLabel = expense?.category?.name ?? t("dashboardUi.expenses.form.categoryNone");

  if (!houseId || !expenseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
          {t("dashboardUi.expenses.backToJournal")}
        </DashboardBackLink>
        <ErrorState onRetry={() => void expenseQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className={cn(DASHBOARD_PAGE_CONTAINER, "flex min-h-[70vh] flex-col")}>
      <DashboardBackLink to={dashboardPaths.houseExpenseDetail(houseId, expenseId)}>
        {t("dashboardUi.expenses.backToExpenseDetail")}
      </DashboardBackLink>
      <h1 className="font-headline text-lg font-bold text-primary">{t("dashboardUi.expenses.paidFeedbackTitle")}</h1>

      <div className="flex flex-grow flex-col items-center text-center">
        <div className="mb-8 flex flex-col items-center">
          <div
            className="mb-6 flex size-24 items-center justify-center rounded-full bg-secondary-container/30 shadow-[0_0_40px_rgba(0,108,73,0.15)]"
            aria-hidden
          >
            <div className="flex size-16 items-center justify-center rounded-full bg-secondary">
              <Icon name="Check" className="size-8 text-white" strokeWidth={2.5} aria-hidden />
            </div>
          </div>
          <h2 className="font-headline text-3xl font-extrabold tracking-tight text-on-surface">
            {t("dashboardUi.expenses.paidFeedbackHero")}
          </h2>
          <p className="mt-2 font-body text-body-md text-on-surface-variant">
            {t("dashboardUi.expenses.paidFeedbackSubline")}
          </p>
        </div>

        <div className="w-full rounded-[1.5rem] border border-outline-variant/15 bg-surface-container-lowest p-8 shadow-sm">
          <div className="text-center">
            <p className="text-label-md font-bold uppercase tracking-wider text-on-surface-variant">
              {t("dashboardUi.expenses.paidFeedbackAmountLabel")}
            </p>
            <p className="mt-1 font-headline text-4xl font-bold tracking-tight text-primary md:text-5xl">
              {formatMinorAsCurrency(effectiveMinor, expense.currency, locale)}
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-6">
            <div className="flex gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Icon name="Receipt" className="size-6" strokeWidth={1.5} aria-hidden />
              </div>
              <div className="min-w-0 text-left">
                <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {t("dashboardUi.expenses.paidFeedbackCategoryLabel")}
                </span>
                <p className="font-body text-base font-semibold text-on-surface">{categoryLabel}</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon name="Users" className="size-6" strokeWidth={1.5} aria-hidden />
              </div>
              <div className="min-w-0 flex-grow text-left">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      {t("dashboardUi.expenses.paidFeedbackPaidByLabel")}
                    </span>
                    <p className="font-body text-base font-semibold text-on-surface">{paidByLabel}</p>
                  </div>
                  {contributionPercent != null ? (
                    <span className="text-label-md font-bold text-primary">
                      {t("dashboardUi.expenses.paidFeedbackContribution", { percent: contributionPercent })}
                    </span>
                  ) : null}
                </div>
                {contributionPercent != null ? (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-container-highest shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]">
                    <div
                      className="relative h-full rounded-full bg-gradient-to-r from-primary to-primary-container"
                      style={{ width: `${Math.min(100, Math.max(0, contributionPercent))}%` }}
                    >
                      <div className="absolute inset-0 bg-white/10 blur-[1px]" />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-surface-container text-on-surface-variant">
                <Icon name="Calendar" className="size-6" strokeWidth={1.5} aria-hidden />
              </div>
              <div className="min-w-0 text-left">
                <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {t("dashboardUi.expenses.paidFeedbackDateLabel")}
                </span>
                <p className="font-body text-base font-semibold text-on-surface">{formatShort(expense.occurredOn)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex w-full flex-col gap-3">
          <Button className="w-full font-headline font-bold shadow-lg shadow-primary/10" asChild>
            <Link to={dashboardPaths.home}>{t("dashboardUi.expenses.paidFeedbackBackHome")}</Link>
          </Button>
          <Button variant="outline" className="w-full font-label text-xs font-bold uppercase tracking-widest" asChild>
            <Link to={dashboardPaths.houseExpenseDetail(houseId, expense.id)} className="inline-flex items-center justify-center gap-2">
              {t("dashboardUi.expenses.paidFeedbackViewDetail")}
              <Icon name="ArrowRight" className="size-4" strokeWidth={2} aria-hidden />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
